import { noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorPageInternal, DashboardPieChart, shadCnColors } from 'noddi-ui-web';
import { TireWearInfoContent } from '../../TireInfo/TireWearInfoContent';
import { fleetTreadDepthStatus } from '../constants';
import type { FleetViewFilterInput } from '../fleetViewSummaryFilterSpecs';
import { TireWearChartTranslations } from '../fleetViewTranslations';
import { useFleetViewSummaryFilters } from '../useFleetViewSummaryFilters';

type Season = 'summer' | 'winter';

type Props = {
  filterParams: FleetViewFilterInput;
  season: Season;
  tooltip?: React.ReactNode;
  translations: TireWearChartTranslations;
};

export const TireWearChart = ({ filterParams, season, tooltip, translations: t }: Props) => {
  const { tireWearStatusSummer, setTireWearStatusSummer, tireWearStatusWinter, setTireWearStatusWinter } =
    useFleetViewSummaryFilters();

  const {
    data: summary,
    isPending,
    error: summaryError
  } = noddiAsync.useGet({
    type: URLKeys.getFleetViewSummary,
    input: filterParams
  });

  const tireWearSummary = season === 'summer' ? summary?.tireWearSummer : summary?.tireWearWinter;
  const totalCars = summary?.totalCars ?? 0;
  const carsWithMeasurements = tireWearSummary?.carsWithMeasurements ?? 0;
  const carsWithoutMeasurements = Math.max(0, totalCars - carsWithMeasurements);

  const selectedValues = season === 'summer' ? tireWearStatusSummer : tireWearStatusWinter;
  const setValues = season === 'summer' ? setTireWearStatusSummer : setTireWearStatusWinter;

  const data = [
    {
      name: t.shouldBeReplaced,
      value: fleetTreadDepthStatus.SOON_WORN_OUT,
      count: tireWearSummary?.carsSoonWornOut ?? 0,
      color: shadCnColors.amber[500]
    },
    {
      name: t.mustBeReplaced,
      value: fleetTreadDepthStatus.WORN_OUT,
      count: tireWearSummary?.carsWornOut ?? 0,
      color: shadCnColors.red[500]
    },
    {
      name: t.goodCondition,
      value: fleetTreadDepthStatus.ABOVE_RECOMMENDED,
      count: tireWearSummary?.carsAboveRecommended ?? 0,
      color: shadCnColors.green[500]
    },
    {
      name: t.noMeasurements,
      value: fleetTreadDepthStatus.NO_MEASUREMENTS,
      count: carsWithoutMeasurements,
      color: shadCnColors.slate[400]
    }
  ];

  const title = season === 'summer' ? t.summerTireCondition : t.winterTireCondition;
  const description = t.carsWithMeasurements(carsWithMeasurements, totalCars);

  const getTooltip = () => {
    if (tooltip) {
      return tooltip;
    }

    if (!t.tireWearInfo) {
      return undefined;
    }

    return (
      <TireWearInfoContent
        season={season}
        translations={{
          ...t.tireWearInfo,
          title: season === 'summer' ? t.tireWearInfo.summerTitle : t.tireWearInfo.winterTitle,
          regulationsDescription:
            season === 'summer'
              ? t.tireWearInfo.summerRegulationsDescription
              : t.tireWearInfo.winterRegulationsDescription
        }}
      />
    );
  };

  if (summaryError) {
    return <ApiErrorPageInternal error={summaryError} />;
  }

  return (
    <DashboardPieChart
      title={title}
      description={description}
      data={data}
      emptyStateTitle={t.noMeasurementsAvailable}
      size='xs'
      selectedValues={selectedValues}
      onSelectionChange={setValues}
      tooltip={getTooltip()}
      isLoading={isPending}
    />
  );
};
